import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from '@rails/activestorage'
import { buildRedactor } from '../redactor'
import Alpine from 'alpinejs'

export default class extends Controller {
  // 通常會設定
  //   data-redactor-direct-upload-url-value="<%= rails_direct_uploads_path %>"
  //   data-redactor-blob-url-template-value="<%= rails_service_blob_path(":signed_id", ":filename") %>"
  static values = {
    directUploadUrl: String,
    blobUrlTemplate: String,
    minHeight: String,
    maxHeight: String
  }

  connect() {
    this.editorApp = buildRedactor(this.element, {
      theme: 'light',
      minHeight: this.minHeightValue,
      maxHeight: this.maxHeightValue,
      image: this.imageUploadSettings,
      subscribe: {
        ...this.refreshSelectImageBoxCallbacks
      },
      classes: {
        tags: {
          'p': 'empty:before:content-["\\00a0"] empty:before:block empty:min-h-[1em]',
        }
      }
    })
    this.addDataForImages()
    this.refreshDataStatesForImages()
    this.watchUploadFinished()
  }

  disconnect() {
    this.editorApp.destroy()
  }

  processFilename(filename) {
    const ext = filename.substring(filename.lastIndexOf('.'))
    const name = filename.substring(0, filename.lastIndexOf('.'))
    // 模擬 Rails 的 parameterize 方法
    const parameterized = name.toLowerCase()
                             .replace(/[^a-z0-9\-_]+/g, '-')
                             .replace(/^-+|-+$/g, '')
    return `${parameterized}${ext.toLowerCase()}`
  }

  get store() {
    this._store ||= Alpine.reactive({
      uploadStates: { status: 'init', result: {} },
      uploadApp: null,
    })
    return this._store
  }

  get uploadStates() {
    return this.store.uploadStates
  }

  get imageUploadSettings() {
    const callbacks = {
      // MUST: because `DirectUpload` will use
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener('progress', ({ loaded, total }) => {
          console.log(Math.floor(loaded * 100.0 / total))
        })
      }
    }

    return {
      upload: (uploadApp, data) => {
        this.store.uploadApp = uploadApp
        this.resetUploadStates()

        for (let key in data.files) {
          if (typeof data.files[key] === 'object') {
            this.uploadStates.result[key] = { progress: 0, id: null, url: '' }

            const instance = new DirectUpload(data.files[key], this.directUploadUrlValue, callbacks)
            instance.create((error, blob) => {
              if (error) {
                this.uploadStates.result[key].progress = 100
                console.error(error)
              } else {
                // Add an appropriately-named hidden input to the form
                // with a value of blob.signed_id
                const processedFilename = this.processFilename(data.files[key].name)
                const blobUrl = this.blobUrlTemplateValue.replace(':signed_id', blob.signed_id)
                                                         .replace(':filename', processedFilename)
                this.uploadStates.result[key] = { progress: 100, id: blob.signed_id, url: blobUrl }
              }
            })
          }
        }
      }
    }
  }

  get refreshSelectImageBoxCallbacks() {
    const callback = () => {
      // const images = Object.values(this.editorApp.image.dataStates)
      // const unSelectedImages = images.filter( image => !image.status )
      //                                .reduce((rs, image) => ({...rs, [image.id]: image}), {})
      const unSelectedImages = this.editorApp.image.dataStates
      this.editorApp.opts.opts.image.select = unSelectedImages
    }

    return {
      'modal.before.open': callback,
      'image.select': callback,
      'image.upload': callback,
      'image.remove': callback,
    }
  }

  // private

  resetUploadStates() {
    this.store.uploadStates = {
      status: 'init',
      result: {},
    }
  }

  // 把editor裡面的image補上[data-image]
  addDataForImages() {
    let utils = this.editorApp.create('utils')
    this.editorApp.editor.getLayout().find('img').each($node => {
      if ( $node.attr('data-image') ) { return }

      $node.attr('data-image', utils.getRandomId())
    })
  }

  // 把圖片狀態更新後，就能在點擊`image button`的modal後，可以選擇已經存在editor裡面的image
  refreshDataStatesForImages() {
    this.editorApp.image.observeStates()
    this.editorApp.opts.opts.image.select = this.editorApp.image.dataStates
  }

  watchUploadFinished() {
    Alpine.watch(
      () => Object.values(this.uploadStates.result).every(rs => rs.progress === 100),
      (isFinished, prevState) => {
        if (!isFinished || isFinished === prevState) { return }

        this.uploadStates.status = 'finished'
      }
    )

    Alpine.watch(
      () => this.uploadStates.status,
      (status) => {
        if (status !== 'finished' || !this.store.uploadApp) { return }

        this.store.uploadApp.complete(this.uploadStates.result)
      }
    )
  }
}
